import { useEffect, useMemo, useState } from 'react'

import { GasParams, GasParamsStringType } from 'types/entities'

import { useNetworkConnection } from 'hooks/listeners'
import { useGasFetcher, useGetBalancesRequest } from 'hooks/api'
import { useSelfGeneratedState, useAirdropTransactions } from 'hooks/airdrop'

import { useIsPendingAirdropTxs } from 'state/user/hooks'
import {
  useAirdropId,
  useSetGasParams,
  useIsSelfGenerated,
  useIsAirdropFailed,
  useTransactionsCount,
  useIsAirdropInterrupted,
} from 'state/airdrop/hooks'

import { createModal, ModalName } from 'modal'

import { convertStringObjToBigInt } from 'utils/helpers'

import { Icon, Loader, Notification } from 'components/ui'

import { TopUp } from './TopUp'
import { Airdrop } from './Airdrop'

import { getIsPendingStep } from './helpers'
import { SendingState, StepName, StepStatus } from './types'
import { CloseButton, Container, Wrap, Wrapper, Text, InfoText } from './Style'

type Props = {
  onCurrentClose?: VoidFunction
  onErrorCallback: (error: string) => void
}

export function AirdropTx({ onCurrentClose, onErrorCallback }: Props) {
  const airdropId = useAirdropId()
  const gasFetcher = useGasFetcher()
  const setGasParams = useSetGasParams()
  const isSelfGenerated = useIsSelfGenerated()
  const balancesRequest = useGetBalancesRequest()
  const selfGeneratedState = useSelfGeneratedState()

  const isOnline = useNetworkConnection()
  const isAirdropFailed = useIsAirdropFailed()
  const transactionsCount = useTransactionsCount()
  const airdropTransactions = useAirdropTransactions()
  const isAirdropInterrupted = useIsAirdropInterrupted()

  const isPendingAirdropTx = useIsPendingAirdropTxs()

  const [error, setError] = useState('')
  const [sendingState, setSendingState] = useState<SendingState | null>(null)

  useEffect(() => {
    balancesRequest.refetch()
  }, [])

  useEffect(() => {
    setDefaultParams()
  }, [])

  const setDefaultParams = async () => {
    const gasParams = convertGasParams()

    if (selfGeneratedState.shouldTopUpPk || !isSelfGenerated) {
      setGasParams(gasParams)
    }

    const bigintGasParams = convertStringObjToBigInt(gasParams) as GasParams

    const shouldTopUpPk = await selfGeneratedState.getShouldTopUpPk(bigintGasParams)
    onSetSendingState({
      status: StepStatus.PENDING,
      name: shouldTopUpPk ? StepName.TOP_UP : StepName.SEND,
    })
  }

  const convertGasParams = () => {
    const data = gasFetcher.data

    const _data = {
      gasPrice: data?.gasPrice,
      maxFeePerGas: data?.maxFeePerGas,
      maxPriorityFeePerGas: data?.maxPriorityFeePerGas,
    } as GasParamsStringType

    return _data
  }

  const sendLoading = useMemo(() => {
    return isPendingAirdropTx || getIsPendingStep(sendingState, StepName.SEND)
  }, [isPendingAirdropTx, sendingState])

  const onSetSendingState = (_sendingState: SendingState) => {
    setSendingState(_sendingState)
  }

  const isTopUp = useMemo(() => sendingState?.name === StepName.TOP_UP, [sendingState?.name])

  const isAirdrop = useMemo(() => sendingState?.name === StepName.SEND, [sendingState?.name])

  const allowCloseButton = useMemo(
    () =>
      error ||
      isTopUp ||
      isAirdropFailed ||
      isAirdropInterrupted ||
      airdropTransactions.transactions.length === transactionsCount,
    [error, isTopUp, airdropTransactions.transactions, transactionsCount, isAirdropFailed, isAirdropInterrupted],
  )

  const showWarningMessage = useMemo(
    () =>
      !error &&
      isAirdrop &&
      airdropTransactions.transactions.length !== transactionsCount &&
      !isAirdropFailed &&
      !isAirdropInterrupted,
    [error, isAirdrop, airdropTransactions.transactions, transactionsCount, isAirdropFailed, isAirdropInterrupted],
  )

  const isFetchingData = useMemo(() => {
    return gasFetcher.isLoading || !airdropId || balancesRequest.isLoading || !sendingState?.name
  }, [gasFetcher.isLoading, airdropId, balancesRequest.isLoading, sendingState?.name])

  return (
    <Wrap>
      <Container>
        {!isOnline && (
          <Notification type='error'>
            No&nbsp;internet connection!
            <br />
            Please check your internet connection.
          </Notification>
        )}

        {(isFetchingData || !sendingState) && (
          <>
            <Wrapper>
              <Loader size={7.2} />
            </Wrapper>
            <Text>Fetching data</Text>
          </>
        )}

        {showWarningMessage && (
          <InfoText>
            We disabled closing button. It will be available again in a few moments.
            <br />
            Please don&rsquo;t close modal window and wait until airdrop is sent.
            <br />
            It&rsquo;s also not recommended to reload the page.
          </InfoText>
        )}

        {!isFetchingData && airdropId && (
          <>
            {isTopUp ? (
              <TopUp
                airdropId={airdropId}
                isLoading={sendLoading}
                sendingState={sendingState}
                setSendingState={onSetSendingState}
              />
            ) : (
              <Airdrop
                error={error}
                setError={setError}
                airdropId={airdropId}
                isLoading={sendLoading}
                sendingState={sendingState}
                onCurrentClose={onCurrentClose}
                onErrorCallback={onErrorCallback}
                setSendingState={onSetSendingState}
              />
            )}
          </>
        )}
      </Container>

      {allowCloseButton && (
        <CloseButton onClick={onCurrentClose}>
          <Icon name='close' size='large' />
        </CloseButton>
      )}
    </Wrap>
  )
}

const newAirdropTxModal = (props: Props) =>
  createModal({
    title: '_airdrop',
    align: 'center',
    name: ModalName.AIRDROP,
    disableOverlayClose: true,
    // @ts-ignore
    container: <AirdropTx {...props} />,
  })

export { newAirdropTxModal }
